import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronDown, ChevronUp, Filter, XCircle, Edit3 } from 'lucide-react';
import { getForecastAccounts } from '../../utils/forecastAccountsApi';
import ForecastAccountCard from './ForecastAccountCard';  // eslint-disable-line @typescript-eslint/no-unused-vars
import ForecastAccountDetail from './ForecastAccountDetail';
import PnLForecastView from './PnLForecastView';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import BulkDriverUpdateModal from './BulkDriverUpdateModal';

const ForecastTable = ({
  budgetData = [],
  forecastData = [],
  chartOfAccounts = [],
  onDataChange,
  assumptions = {},
  location,
  orgCode,  // eslint-disable-line @typescript-eslint/no-unused-vars
  year,
  isForecastVsBudget,  // eslint-disable-line @typescript-eslint/no-unused-vars
  viewMode = 'pnl', // Controlled from parent: 'list' or 'pnl'
  searchTerm = '', // Controlled from parent
  initialStartMonth = 0, // Date range filtering
  initialEndMonth = 11,
  refreshKey = 0, // Trigger refresh from parent
  isRefreshing = false, // Background refresh in progress
  onRefreshComplete // Callback when refresh completes
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [forecastAccounts, setForecastAccounts] = useState({});
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [displayedAccounts, setDisplayedAccounts] = useState(25); // Start with 25 accounts
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false); // Controls detail view visibility
  const [selectedAccountIds, setSelectedAccountIds] = useState(new Set()); // Multi-select
  const [bulkDriverType, setBulkDriverType] = useState(''); // Bulk driver assignment
  const hasSetInitialAccount = useRef(false);
  const accountListRef = useRef(null);
  const headerCheckboxRef = useRef(null);

  // Advanced filters state
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filters, setFilters] = useState({
    accountTypes: [],
    accountGroups: [],
    driverTypes: [],
    accountNumberFrom: '',
    accountNumberTo: '',
    hasDriver: null,
    missingDriver: false
  });

  // Bulk selection state
  const [isBulkUpdateModalOpen, setIsBulkUpdateModalOpen] = useState(false);

  const orgIdToUse = location?.orgId || null;
  const locationIdToUse = location?.id;

  // Load forecast accounts (driver configuration from SQL forecastAccounts table)
  const loadForecastAccounts = useCallback(async () => {
    if (!orgIdToUse || !locationIdToUse) return;

    try {
      console.log('Loading forecast accounts (driver config) from SQL');
      const accounts = await getForecastAccounts({
        orgId: orgIdToUse,
        locationId: locationIdToUse
      });
      console.log('Loaded forecast accounts:', accounts);

      const accountsMap = {};
      if (accounts && Array.isArray(accounts)) {
        accounts.forEach(account => {
          // Skip deleted records
          if (account && account.chartOfAccountId && !account.isDeleted) {
            console.log(`Account ${account.chartOfAccountId}: forecastDriver = ${account.forecastDriver}`);
            accountsMap[account.chartOfAccountId] = account;
          }
        });
      }

      setForecastAccounts(accountsMap);
      return accountsMap;
    } catch (error) {
      console.error('Error loading forecast accounts:', error);
      return {};
    }
  }, [orgIdToUse, locationIdToUse]);

  // Process chart of accounts with forecast settings - FILTER BY LOCATION
  const processedAccounts = useMemo(() => {
    if (!Array.isArray(chartOfAccounts) || !location || !orgIdToUse || !year || !locationIdToUse) {
      return [];
    }

    // Filter accounts to only show those matching the selected location
    return chartOfAccounts
      .filter(account => {
        // If account has locationId, it must match the selected location
        // If account has no locationId, it's available to all locations (org-level account)
        return !account.locationId || account.locationId === locationIdToUse;
      })
      .map(account => ({
        id: account.id,
        accountNumber: account.accountNumber,
        accountName: account.accountName,
        accountType: account.accountType,
        accountGroup: account.accountGroup,
        chartOfAccountId: account.id,
        locationId: locationIdToUse,
        driverType: forecastAccounts[account.id]?.forecastDriver || '',
        driverInput: forecastAccounts[account.id]?.forecastInput || '',
        inputMode: forecastAccounts[account.id]?.inputMode || 'monthly'
      }));
  }, [chartOfAccounts, forecastAccounts, location, orgIdToUse, locationIdToUse, year]);

  // Extract unique filter options from processed accounts
  const filterOptions = useMemo(() => {
    const types = new Set();
    const groups = new Set();
    const drivers = new Set();

    processedAccounts.forEach(account => {
      if (account.accountType) types.add(account.accountType);
      if (account.accountGroup) groups.add(account.accountGroup);
      if (account.driverType) drivers.add(account.driverType);
    });

    return {
      accountTypes: Array.from(types).sort(),
      accountGroups: Array.from(groups).sort(),
      driverTypes: Array.from(drivers).sort()
    };
  }, [processedAccounts]);

  // Detect category-level drivers (RMS Forecast, OTB Forecast) for account groups
  // This is used to lock individual accounts when a category-level driver is active
  const categoryDriverInfo = useMemo(() => {
    const info = {};

    // Category-level drivers that should apply to entire account groups
    const categoryDrivers = ['RMS Forecast', 'OTB Forecast'];

    processedAccounts.forEach(account => {
      if (account.accountGroup && categoryDrivers.includes(account.driverType)) {
        // This account group has a category-level driver
        if (!info[account.accountGroup]) {
          info[account.accountGroup] = {
            driver: account.driverType,
            controllingAccount: {
              id: account.id,
              accountNumber: account.accountNumber,
              accountName: account.accountName
            },
            affectedAccountIds: []
          };
        }

        // Track all accounts in this group
        info[account.accountGroup].affectedAccountIds.push(account.id);
      }
    });

    // Now add all accounts in groups with category drivers to the affected list
    processedAccounts.forEach(account => {
      if (account.accountGroup && info[account.accountGroup]) {
        if (!info[account.accountGroup].affectedAccountIds.includes(account.id)) {
          info[account.accountGroup].affectedAccountIds.push(account.id);
        }
      }
    });

    return info;
  }, [processedAccounts]);

  // Filter accounts based on search term and advanced filters
  const filteredAccounts = useMemo(() => {
    let filtered = processedAccounts;

    // Apply search term filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(account =>
        account.accountNumber.toLowerCase().includes(searchLower) ||
        account.accountName.toLowerCase().includes(searchLower) ||
        account.accountType?.toLowerCase().includes(searchLower) ||
        account.driverType?.toLowerCase().includes(searchLower)
      );
    }

    // Apply account type filter
    if (filters.accountTypes.length > 0) {
      filtered = filtered.filter(account =>
        filters.accountTypes.includes(account.accountType)
      );
    }

    // Apply account group filter
    if (filters.accountGroups.length > 0) {
      filtered = filtered.filter(account =>
        filters.accountGroups.includes(account.accountGroup)
      );
    }

    // Apply driver type filter
    if (filters.driverTypes.length > 0) {
      filtered = filtered.filter(account =>
        filters.driverTypes.includes(account.driverType)
      );
    }

    // Apply account number range filter
    if (filters.accountNumberFrom) {
      filtered = filtered.filter(account =>
        account.accountNumber >= filters.accountNumberFrom
      );
    }
    if (filters.accountNumberTo) {
      filtered = filtered.filter(account =>
        account.accountNumber <= filters.accountNumberTo
      );
    }

    // Apply missing driver filter
    if (filters.missingDriver) {
      filtered = filtered.filter(account =>
        !account.driverType || account.driverType === ''
      );
    }

    // Apply has driver filter (if explicitly set to true or false)
    if (filters.hasDriver !== null) {
      if (filters.hasDriver) {
        filtered = filtered.filter(account =>
          account.driverType && account.driverType !== ''
        );
      } else {
        filtered = filtered.filter(account =>
          !account.driverType || account.driverType === ''
        );
      }
    }

    return filtered;
  }, [processedAccounts, searchTerm, filters]);

  // Paginated accounts for display
  const paginatedAccounts = useMemo(() => {
    return filteredAccounts.slice(0, displayedAccounts);
  }, [filteredAccounts, displayedAccounts]);

  // Load forecast accounts on mount
  useEffect(() => {
    const loadData = async () => {
      if (!orgIdToUse || !locationIdToUse) return;

      setIsLoading(true);
      try {
        await loadForecastAccounts();
      } catch (error) {
        console.error('Error in loadData:', error);
      } finally {
        setTimeout(() => setIsLoading(false), 200);
      }
    };

    loadData();
  }, [orgIdToUse, locationIdToUse, loadForecastAccounts]);

  // Set default selected account (first one) - only once when accounts load
  useEffect(() => {
    if (paginatedAccounts.length > 0 && !hasSetInitialAccount.current) {
      setSelectedAccount(paginatedAccounts[0]);
      hasSetInitialAccount.current = true;
    }
  }, [paginatedAccounts]);

  // Reset the flag and pagination when location or search changes
  useEffect(() => {
    hasSetInitialAccount.current = false;
    setSelectedAccount(null);
    setDisplayedAccounts(25); // Reset to initial page size
  }, [locationIdToUse, orgIdToUse, searchTerm]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback((e) => {
    const element = e.target;
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;

    if (isNearBottom && !isLoadingMore && paginatedAccounts.length < filteredAccounts.length) {
      setIsLoadingMore(true);
      // Load 25 more accounts
      setTimeout(() => {
        setDisplayedAccounts(prev => Math.min(prev + 25, filteredAccounts.length));
        setIsLoadingMore(false);
      }, 300); // Small delay to prevent rapid firing
    }
  }, [isLoadingMore, paginatedAccounts.length, filteredAccounts.length]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.accountTypes.length > 0) count++;
    if (filters.accountGroups.length > 0) count++;
    if (filters.driverTypes.length > 0) count++;
    if (filters.accountNumberFrom || filters.accountNumberTo) count++;
    if (filters.missingDriver) count++;
    if (filters.hasDriver !== null) count++;
    return count;
  }, [filters]);

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      accountTypes: [],
      accountGroups: [],
      driverTypes: [],
      accountNumberFrom: '',
      accountNumberTo: '',
      hasDriver: null,
      missingDriver: false
    });
  };

  // Toggle a filter value in an array filter
  const toggleFilterValue = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: prev[filterKey].includes(value)
        ? prev[filterKey].filter(v => v !== value)
        : [...prev[filterKey], value]
    }));
  };

  // Handle account selection
  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    setShowDetailView(true);
    // Update URL params with selected account
    if (account) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('accountId', account.id);
        return newParams;
      });
    }
  };

  // Handle back from detail view
  const handleBackToList = () => {
    setShowDetailView(false);
    setSelectedAccount(null);
    // Clear account ID from URL
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('accountId');
      return newParams;
    });
  };

  // Handle checkbox selection
  const handleCheckboxToggle = (accountId, event) => {
    event.stopPropagation(); // Prevent opening detail view
    setSelectedAccountIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  // Select all filtered accounts (not just visible ones)
  const handleSelectAll = () => {
    if (selectedAccountIds.size === filteredAccounts.length) {
      setSelectedAccountIds(new Set());
    } else {
      setSelectedAccountIds(new Set(filteredAccounts.map(acc => acc.id)));
    }
  };

  // Handle bulk driver assignment
  const handleBulkDriverAssignment = async () => {
    if (selectedAccountIds.size === 0 || !bulkDriverType) {
      return;
    }

    const { upsertBulkForecastAccounts } = await import('../../utils/forecastAccountsApi');

    try {
      setIsLoading(true);

      const accountsToUpdate = Array.from(selectedAccountIds).map(accountId => ({
        orgId: orgIdToUse,
        locationId: locationIdToUse,
        chartOfAccountId: accountId,
        forecastDriver: bulkDriverType,
        inputMode: 'monthly'
      }));

      await upsertBulkForecastAccounts(accountsToUpdate);

      // Reload forecast accounts
      await loadForecastAccounts();

      // Clear selections
      setSelectedAccountIds(new Set());
      setBulkDriverType('');

      // Notify parent
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      console.error('Error updating bulk drivers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Restore selected account from URL on load
  useEffect(() => {
    const accountId = searchParams.get('accountId');
    if (accountId && paginatedAccounts.length > 0 && !selectedAccount) {
      const accountFromUrl = paginatedAccounts.find(acc => acc.id === accountId);
      if (accountFromUrl) {
        setSelectedAccount(accountFromUrl);
        hasSetInitialAccount.current = true;
      }
    }
  }, [searchParams, paginatedAccounts, selectedAccount]);

  // Handle data change from detail view
  const handleDetailDataChange = useCallback(async () => {
    // Reload forecast accounts to get updated data
    await loadForecastAccounts();

    // Notify parent component
    if (onDataChange) {
      onDataChange();
    }
  }, [loadForecastAccounts, onDataChange]);

  // Update selected account when forecast accounts data changes
  useEffect(() => {
    if (selectedAccount && processedAccounts.length > 0) {
      const updatedAccount = processedAccounts.find(acc => acc.id === selectedAccount.id);
      if (updatedAccount && updatedAccount.driverType !== selectedAccount.driverType) {
        setSelectedAccount(updatedAccount);
      }
    }
  }, [processedAccounts, selectedAccount]);

  // Toggle account selection for bulk operations
  const toggleAccountSelection = useCallback((accountId) => {
    setSelectedAccountIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  }, []);

  // Select all filtered accounts
  const selectAllAccounts = useCallback(() => {
    const allIds = new Set(filteredAccounts.map(acc => acc.id));
    setSelectedAccountIds(allIds);
  }, [filteredAccounts]);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedAccountIds(new Set());
  }, []);

  // Get selected accounts objects
  const selectedAccounts = useMemo(() => {
    return processedAccounts.filter(acc => selectedAccountIds.has(acc.id));
  }, [processedAccounts, selectedAccountIds]);

  // Handle bulk driver update
  const handleBulkDriverUpdate = useCallback(async (newDriver, accounts) => {
    const { upsertForecastAccount } = await import('../../utils/forecastAccountsApi');

    // Update each account with the new driver
    const updatePromises = accounts.map(account =>
      upsertForecastAccount({
        orgId: orgIdToUse,
        locationId: locationIdToUse,
        chartOfAccountId: account.chartOfAccountId,
        forecastDriver: newDriver,
        forecastInput: 0, // Reset input when changing driver
        inputMode: 'monthly'
      })
    );

    await Promise.all(updatePromises);

    // Reload forecast accounts
    await loadForecastAccounts();

    // Clear selection
    clearSelection();

    // Notify parent
    if (onDataChange) {
      onDataChange();
    }
  }, [orgIdToUse, locationIdToUse, loadForecastAccounts, clearSelection, onDataChange]);

  // Navigate accounts with arrow keys
  const navigateAccounts = useCallback((direction) => {
    if (!selectedAccount || paginatedAccounts.length === 0) return;

    const currentIndex = paginatedAccounts.findIndex(acc => acc.id === selectedAccount.id);
    if (currentIndex === -1) return;

    let newIndex;
    if (direction === 'up') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
    } else {
      newIndex = currentIndex < paginatedAccounts.length - 1 ? currentIndex + 1 : currentIndex;
    }

    if (newIndex !== currentIndex) {
      handleAccountSelect(paginatedAccounts[newIndex]);
    }
  }, [selectedAccount, paginatedAccounts, handleAccountSelect]);

  // Keyboard shortcuts configuration
  const shortcuts = useMemo(() => ({
    'ctrl+f': {
      handler: () => {
        const searchInput = document.querySelector('input[type="text"][placeholder="Search accounts..."]');
        searchInput?.focus();
      },
      description: 'Focus search'
    },
    'arrowup': {
      handler: () => navigateAccounts('up'),
      description: 'Select previous account'
    },
    'arrowdown': {
      handler: () => navigateAccounts('down'),
      description: 'Select next account'
    },
    'escape': {
      handler: () => {
        if (filtersExpanded) {
          setFiltersExpanded(false);
        }
      },
      description: 'Close filters'
    },
    'ctrl+b': {
      handler: () => setFiltersExpanded(prev => !prev),
      description: 'Toggle filters'
    }
  }), [navigateAccounts, filtersExpanded]);

  // Enable keyboard shortcuts
  const { showHelp, setShowHelp, getShortcutList } = useKeyboardShortcuts(shortcuts, true);

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Conditional View Rendering */}
      {viewMode === 'pnl' ? (
        <PnLForecastView
          chartOfAccounts={chartOfAccounts}
          forecastData={forecastData}
          budgetData={budgetData}
          forecastAccounts={forecastAccounts}
          orgId={orgIdToUse}
          locationId={locationIdToUse}
          currency={location?.currency || 'USD'}
          year={year}
          onDataChange={loadForecastAccounts}
          initialStartMonth={initialStartMonth}
          initialEndMonth={initialEndMonth}
          refreshKey={refreshKey}
          isRefreshing={isRefreshing}
          onRefreshComplete={onRefreshComplete}
        />
      ) : showDetailView && selectedAccount ? (
        // Detail View - Full Screen
        <ForecastAccountDetail
          account={selectedAccount}
          forecastData={forecastData}
          budgetData={budgetData}
          orgId={orgIdToUse}
          locationId={locationIdToUse}
          currency={location?.currency || 'USD'}
          year={year}
          onDataChange={handleDetailDataChange}
          onBack={handleBackToList}
          categoryDriverInfo={categoryDriverInfo}
          forecastDriverExplanations={{
            'Static Entry': 'Forecast value is manually entered for each month. If no value is entered, it may pull from the budget.',
            '$ / ORN': 'Forecast is calculated by multiplying a user-defined rate by the number of Occupied Room Nights for each month (e.g., $2.00/ORN * 500 ORN = $1,000).',
            '$ / ARN': 'Forecast is calculated by multiplying a user-defined rate by the number of Available Room Nights for each month.',
            'Total Guests': 'Forecast is calculated by multiplying a user-defined driver value by the number of guests for each month.',
            '$ / ARN LM': "Forecast is based on last month's actual calculated dollars per Available Room Night, applied to the current month's Available Room Nights.",
            '$ / ORN LM': "Forecast is based on last month's actual calculated dollars per Occupied Room Night, applied to the current month's Occupied Room Nights.",
            '$ LM': "Forecast uses the total actual booked amount from the previous month.",
            'Integrated Driver': 'Forecast data is automatically pulled from integrated external systems (e.g., labor management, revenue management systems). No manual entry is typically required.',
            'Ledger Integration': 'Forecast initially uses budgeted values. Once actual financial data is posted from an integrated ledger system for a period, the forecast for that period is replaced by the actuals.',
            'OTB Forecast': 'On-The-Books forecast revenue from the hotel occupancy data. This driver applies to all accounts in the category.',
            'RMS Forecast': 'Revenue Management System forecast revenue from hotel occupancy data. This driver applies to all accounts in the category.'
          }}
        />
      ) : (
        // List View - Simplified with Multi-select
        <div className="flex flex-col flex-1 min-h-0">
          {/* Compact Bulk Actions Bar */}
          {selectedAccountIds.size > 0 && (
            <div className="mb-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">
                  {selectedAccountIds.size} selected
                </span>
                <select
                  value={bulkDriverType}
                  onChange={(e) => setBulkDriverType(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Assign driver...</option>
                  <option value="$ / ARN">$ / ARN</option>
                  <option value="$ / ARN LM">$ / ARN LM</option>
                  <option value="$ / ARN LY">$ / ARN LY</option>
                  <option value="$ / ORN">$ / ORN</option>
                  <option value="$ / ORN LM">$ / ORN LM</option>
                  <option value="$ / ORN LY">$ / ORN LY</option>
                  <option value="$ LM">$ LM</option>
                  <option value="$POR Projection">$POR Projection</option>
                  <option value="Budget">Budget</option>
                  <option value="LY Same Month">LY Same Month</option>
                  <option value="% of Revenue">% of Revenue</option>
                  <option value="% of Selected Revenue">% of Selected Revenue</option>
                  <option value="REVPAR Projection">REVPAR Projection</option>
                  <option value="Static Entry">Static Entry</option>
                </select>
                <button
                  onClick={handleBulkDriverAssignment}
                  disabled={!bulkDriverType}
                  className="px-3 py-1 bg-orange-600 text-white text-sm font-medium rounded hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Apply
                </button>
              </div>
              <button
                onClick={() => setSelectedAccountIds(new Set())}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            </div>
          )}

          {/* Compact Header */}
          <div className="flex items-center justify-between mb-2">
            {/* Results count */}
            {searchTerm && (
              <span className="text-xs text-gray-500">
                {filteredAccounts.length} accounts found
              </span>
            )}
            {!searchTerm && (
              <span className="text-xs text-gray-500">
                {paginatedAccounts.length} of {filteredAccounts.length} accounts
              </span>
            )}

            {/* Select All */}
            {filteredAccounts.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-xs text-orange-600 hover:text-orange-700 font-medium"
              >
                {selectedAccountIds.size === filteredAccounts.length ? 'Deselect All' : `Select All (${filteredAccounts.length})`}
              </button>
            )}
          </div>

          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="mt-2 flex items-center gap-2 text-sm text-gray-700 hover:text-orange-600 transition-colors w-full"
          >
            <Filter size={16} />
            <span className="font-medium">Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
            {filtersExpanded ? <ChevronUp size={16} className="ml-auto" /> : <ChevronDown size={16} className="ml-auto" />}
          </button>

          {/* Advanced Filters Panel */}
          {filtersExpanded && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
              {/* Account Type Filter */}
              {filterOptions.accountTypes.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Account Type</label>
                  <div className="space-y-1">
                    {filterOptions.accountTypes.map(type => (
                      <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.accountTypes.includes(type)}
                          onChange={() => toggleFilterValue('accountTypes', type)}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Account Group Filter */}
              {filterOptions.accountGroups.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Account Group</label>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {filterOptions.accountGroups.map(group => (
                      <label key={group} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.accountGroups.includes(group)}
                          onChange={() => toggleFilterValue('accountGroups', group)}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-gray-700">{group}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Driver Type Filter */}
              {filterOptions.driverTypes.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Driver Type</label>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {filterOptions.driverTypes.map(driver => (
                      <label key={driver} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.driverTypes.includes(driver)}
                          onChange={() => toggleFilterValue('driverTypes', driver)}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-gray-700">{driver || '(No Driver)'}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Account Number Range */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Account Number Range</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="From"
                    value={filters.accountNumberFrom}
                    onChange={(e) => setFilters(prev => ({ ...prev, accountNumberFrom: e.target.value }))}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="text"
                    placeholder="To"
                    value={filters.accountNumberTo}
                    onChange={(e) => setFilters(prev => ({ ...prev, accountNumberTo: e.target.value }))}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
                  />
                </div>
              </div>

              {/* Missing Driver Toggle */}
              <div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.missingDriver}
                    onChange={(e) => setFilters(prev => ({ ...prev, missingDriver: e.target.checked }))}
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="font-medium text-gray-700">Missing Driver Only</span>
                </label>
              </div>

              {/* Clear Filters Button */}
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium w-full justify-center py-1.5 border border-orange-200 rounded bg-white hover:bg-orange-50 transition-colors"
                >
                  <XCircle size={16} />
                  Clear All Filters
                </button>
              )}
            </div>
          )}

          {/* Results Count */}
          {(searchTerm || activeFilterCount > 0) && (
            <div className="mt-2 text-xs text-gray-500">
              {filteredAccounts.length} {filteredAccounts.length === 1 ? 'account' : 'accounts'} found
              {paginatedAccounts.length < filteredAccounts.length && (
                <span className="ml-1">(showing {paginatedAccounts.length})</span>
              )}
            </div>
          )}

          {/* Bulk Actions */}
          {selectedAccountIds.size > 0 ? (
            <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-orange-900">
                  {selectedAccountIds.size} selected
                </span>
                <button
                  onClick={clearSelection}
                  className="text-xs text-orange-600 hover:text-orange-700 underline"
                >
                  Clear
                </button>
              </div>
              <button
                onClick={() => setIsBulkUpdateModalOpen(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
              >
                <Edit3 size={16} />
                Update Drivers
              </button>
            </div>
          ) : (
            <div className="mt-3">
              <button
                onClick={selectAllAccounts}
                className="text-xs text-orange-600 hover:text-orange-700 font-medium"
              >
                Select all {filteredAccounts.length} account{filteredAccounts.length !== 1 ? 's' : ''}
              </button>
            </div>
          )}

          {/* Account Cards List */}
        <div
          ref={accountListRef}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : paginatedAccounts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? (
                <p>No accounts match "{searchTerm}"</p>
              ) : (
                <p>No accounts available for this location</p>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left w-12">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        checked={selectedAccountIds.size === filteredAccounts.length && filteredAccounts.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Account</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Driver</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedAccounts.map(account => {
                    const categoryInfo = account.accountGroup ? categoryDriverInfo[account.accountGroup] : null;
                    const isLockedByCategory = categoryInfo && categoryInfo.affectedAccountIds.includes(account.id);
                    const isControllingAccount = categoryInfo && categoryInfo.controllingAccount.id === account.id;

                    // Determine account type color
                    const getTypeColor = (accountType) => {
                      switch (accountType?.toLowerCase()) {
                        case 'revenue':
                          return 'text-green-700 bg-green-50 border-green-200';
                        case 'expense':
                          return 'text-red-700 bg-red-50 border-red-200';
                        case 'asset':
                          return 'text-blue-700 bg-blue-50 border-blue-200';
                        default:
                          return 'text-gray-700 bg-gray-50 border-gray-200';
                      }
                    };

                    return (
                      <tr
                        key={account.id}
                        onClick={() => handleAccountSelect(account)}
                        className={`cursor-pointer transition-colors ${
                          selectedAccount?.id === account.id
                            ? 'bg-orange-50'
                            : isLockedByCategory && !isControllingAccount
                              ? 'bg-blue-50/30 hover:bg-blue-50'
                              : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-3 w-12">
                          <input
                            type="checkbox"
                            checked={selectedAccountIds.has(account.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleAccountSelection(account.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-sm font-semibold text-gray-900">{account.accountNumber}</span>
                            <span className="text-sm text-gray-600 truncate max-w-xs">{account.accountName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded border ${getTypeColor(account.accountType)}`}>
                            {account.accountType}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {account.driverType ? (
                            <span className={`text-sm ${isLockedByCategory ? 'font-semibold text-blue-700' : 'text-gray-700'}`}>
                              {account.driverType}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400 italic">No driver</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {isLoadingMore && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                </div>
              )}
              {/* Load More button as fallback when scroll doesn't trigger */}
              {!isLoadingMore && paginatedAccounts.length < filteredAccounts.length && (
                <div className="flex items-center justify-center py-4 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setIsLoadingMore(true);
                      setTimeout(() => {
                        setDisplayedAccounts(prev => Math.min(prev + 50, filteredAccounts.length));
                        setIsLoadingMore(false);
                      }, 300);
                    }}
                    className="px-4 py-2 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    Load More ({filteredAccounts.length - paginatedAccounts.length} remaining)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default React.memo(ForecastTable);
